<template>
  <div align="Left">
    <md-card id="admins" align="Left">
      <md-card-header class="md-card-header-icon md-card-header-green">
        <div class="card-icon">
          <i class="material-icons">group</i>
        </div>
        <h4 class="title">Admins</h4>

        <div class="md-layout md-gutter md-layout-item md-size-100 md-alignment-center-left" style="padding: 0;">
          <div class="md-layout md-gutter md-layout-item md-size-20">
            <md-field class="md-sm">
              <label>Search</label>
              <md-input v-model="searchQuery" @keyup.enter="handleSearch" />
            </md-field>
          </div>
          <div class="md-layout md-gutter md-layout-item md-size-20" v-if="organizations.length > 2">
            <md-field class="md-sm">
              <label for="currentType">Organization</label>
              <md-select v-model="currentOrganization" name="organization">
                <md-option v-for="(type, index) in organizations" :key="index" :value="type.value">
                  {{ type.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout md-gutter md-layout-item md-size-20">
            <md-button class="md-icon-button md-primary" @click="handleSearch">
              <md-icon>search</md-icon>
            </md-button>
          </div>
          <div class="md-layout md-gutter md-layout-item md-alignment-center-right" :class="[
            { 'md-size-40': organizations.length > 2 },
            { 'md-size-60': organizations.length <= 2 }
          ]">
            <md-button class="md-icon-button md-primary" @click="handleAdd">
              <md-icon>add</md-icon>
              <md-tooltip md-direction="bottom">Add new admin</md-tooltip>
            </md-button>
            <md-button class="md-icon-button" @click="handleRefresh">
              <md-icon>refresh</md-icon>
              <md-tooltip md-direction="bottom">Refresh list</md-tooltip>
            </md-button>
          </div>
        </div>
      </md-card-header>

      <md-card-content style="padding-top: 0px">
        <md-table v-if="!isLoading" :value="tableData" class="table-shopping table-striped table-hover">
          <md-table-row>
            <md-table-empty-state :md-description="`No admins found for this search value`" md-label="No admins found">
            </md-table-empty-state>
          </md-table-row>
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Name">
              <span>{{ (item.person.firstName || "-") + " " + (item.person.lastName || "-") }}</span>
            </md-table-cell>

            <md-table-cell md-label="Email">
              {{ item.person.email || "-" }}
            </md-table-cell>

            <md-table-cell md-label="Phone">{{ item.person.cellNumber || "-" }}</md-table-cell>
            <md-table-cell md-label="Status">{{ defineStatus(item.status) }}</md-table-cell>
            <md-table-cell md-label="Actions">
              <div style="display: flex">
                <md-button v-if="isAdmin" style="margin-right: 0.5rem" class="md-icon-button md-primary"
                  @click="handleActivate(item)">
                  <md-icon>{{ item.status == 2 ? "close" : "check" }}</md-icon>
                  <md-tooltip md-direction="bottom">
                    {{ item.status == 2 ? "Deactivate" : "Activate" }}
                  </md-tooltip>
                </md-button>
                <md-button class="md-icon-button" @click="handleEdit(item.ID)">
                  <md-icon>edit</md-icon>
                  <md-tooltip md-direction="bottom">Edit admin</md-tooltip>
                </md-button>
                <md-button style="margin-left: 0.5rem" class="md-icon-button md-danger" @click="handleRemove(item.ID)">
                  <md-icon>delete</md-icon>
                  <md-tooltip md-direction="bottom">Remove admin</md-tooltip>
                </md-button>
                <md-button v-if="isSuperAdmin" style="margin-left: 0.5rem" class="md-icon-button md-primary"
                  @click="handleOrgEdit(item)">
                  <md-icon>business</md-icon>
                  <md-tooltip md-direction="bottom">Edit organizations</md-tooltip>
                </md-button>
                <md-button class="md-icon-button" style="margin-left: 0.5rem" @click="handleChangePassword(item)">
                  <md-icon>manage_accounts</md-icon>
                  <md-tooltip md-direction="bottom">Change password</md-tooltip>
                </md-button>
              </div>
            </md-table-cell>
          </md-table-row>
        </md-table>

        <hr v-if="!isLoading" />
        <div v-if="!isLoading" style="display: flex; align-items: center">
          <div class="pagination-container" style="flex: 1; justify-content: start; height: 50%">
            <a :disabled="offset === 0" class="page-link" style="color: #999" aria-label="Previous" @click="prevPage">
              <i class="fas fa-angle-double-left" />
            </a>
            <span class="page-link"
              style="background-color: #4caf50; color: white; padding: 5px 10px; border-radius: 16px">{{ currentPage
              }}</span>
            <a class="page-link" aria-label="Next" style="color: #999" @click="nextPage">
              <i class="fas fa-angle-double-right" />
            </a>
          </div>
          <div class="table table-stats" style="display: flex; justify-content: center; align-items: start; flex: 2">
            <div class="text-right">
              <p class="card-category">
                Showing from
                <b><u>{{ startAdmin }}</u></b>
                to
                <b><u>{{ endAdmin }}</u></b>. Total of
                <b><u>{{ totalAdmins }}</u></b>
                admins.
              </p>
            </div>
          </div>
          <md-field style="flex: 1; margin-top: 1.5rem">
            <label for="pages">Per page</label>
            <md-select v-model="pagination.perPage" name="pages" @change="updateLimit">
              <md-option v-for="item in pagination.perPageOptions" :key="item" :value="item">
                {{ item }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </md-card-content>
    </md-card>

    <loader :is-visible="isLoading || isLoadingAdmin" />
    <RegisterAdmin v-if="isModalOpen" :is-edit="isEdit" :is-modal="true" :is-create-admin="true" @close="handleClose"
      @complete="handleSubmit" />
    <ChooseOrganizationModal v-if="isOrganizationModalOpen" :is-open="isOrganizationModalOpen"
      @cancel="closeOrganizationModal" @save="handleOrganizationSelected" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import loader from "@/components/GlobeChek/loader.vue"
import Swal from "sweetalert2"
import RegisterAdmin from "@/pages/Auth/RegisterAdmin.vue"
import ChooseOrganizationModal from "@/components/modals/ChooseOrgModal.vue"

export default {
  components: {
    loader,
    RegisterAdmin,
    ChooseOrganizationModal
  },
  data() {
    return {
      isAdmin: (sessionStorage.getItem("usertype") == "Super" || sessionStorage.getItem("usertype") === 'Administrator'),
      isSuperAdmin: sessionStorage.getItem("usertype") == "Super",
      isLoadingAdmin: false,
      defineStatus: (status) => {
        switch (status) {
          case 1:
          case "1":
            return "Pending"
          case 2:
          case "2":
            return "Active"
          case 3:
          case "3":
            return "Inactive"
          default:
            return " - "
        }
      },
      isOrganizationModalOpen: false,
      selectedOrganization: null,
      selectedAdmin: null,
      isEdit: false,
      isModalOpen: false,
      tableData: [],
      searchQuery: "",
      searchedData: [],
      isLoading: false,
      offset: 0,
      limit: 50,
      pagination: {
        perPage: 50,
        perPageOptions: [10, 20, 50, 100, 200]
      },
      currentOrganization: JSON.parse(sessionStorage.getItem("organizations")).length === 1 ? JSON.parse(sessionStorage.getItem("organizations"))[0].orgId : (localStorage.getItem("currentOrgSelectedForFilters") || "all"),
      organizations: [
        { label: 'All', value: 'all' },
        ...JSON.parse(sessionStorage.getItem('organizations')).map(i => {
          return {
            label: i.orgName,
            value: i.orgId
          }
        }),
      ],
    }
  },
  computed: {
    ...mapGetters(["adminsList", "totalAdmins", "adminReg"]),
    filteredAdmins() {
      if (!this.tableData) return []
      return this.tableData.filter((admin) =>
        Object.values(admin).some(
          (value) => typeof value === "string" && value.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      )
    },
    currentPage() {
      return Math.floor(this.offset / this.limit) + 1
    },
    hasMoreData() {
      return this.offset + this.limit < this.totalAdmins
    },
    startAdmin() {
      return this.totalAdmins === 0 ? 0 : this.offset + 1
    },
    endAdmin() {
      return Math.min(this.offset + this.limit, this.totalAdmins)
    }
  },
  watch: {
    "pagination.perPage"(newLimit) {
      this.limit = newLimit
      this.offset = 0
      this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
    }
  },
  mounted() {
    this.$store.dispatch("clearAdminReg")
    this.isLoading = true
    this.fetchAdmins({ orgId: this.currentOrganization })
  },
  methods: {
    ...mapActions(["listAdmins", "listAdminById", "removeAdmin", "updateAdmin", "createAdmin", "updateUserStatus"]),
    async handleChangePassword(user) {
      this.isLoadingAdmin = true
      const detailedAdmin = await this.listAdminById({ id: user.ID })
      this.$emit("openChangePasswordModal", { name: `${detailedAdmin.admin.person.firstName} ${detailedAdmin.admin.person.lastName}`, personId: detailedAdmin.admin.person.id })
      this.isLoadingAdmin = false
    },
    async handleActivate(admin) {
      const payload = {
        userId: admin.ID,
        status: admin.status === 2 ? 3 : 2
      }

      try {
        const res = await this.updateUserStatus({ payload, userType: "admin" })
        if (res) {
          this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
        }
      } catch (error) {
        console.error(error)
        Swal.fire({
          title: "Error!",
          text: "An error occurred while updating the admin status.",
          icon: "error"
        })
      }
    },
    async fetchAdmins({ search = null, orgId = null } = {}) {
      this.isLoading = true
      try {
        const response = await this.listAdmins({
          offset: this.offset,
          limit: this.limit,
          search: search,
          orgId: orgId != 'all' ? orgId : null
        })
        this.tableData = response.admins
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    updateLimit(newLimit) {
      this.offset = 0
      this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    handleSearch() {
      this.offset = 0
      localStorage.setItem('currentOrgSelectedForFilters', this.currentOrganization)
      this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    definePayload() {
      const orgID = this.selectedOrganization
      const payload = {
        orgID: orgID,
        person: {
          firstName: this.adminReg.person.firstName,
          lastName: this.adminReg.person.lastName,
          email: this.adminReg.person.email,
          cellNumber: this.adminReg.person.cellPhone,
          gender: this.adminReg.person.gender
        },
        status: 2,
        address: {
          address1: this.adminReg.address.address1,
          address2: this.adminReg.address.address2,
          city: this.adminReg.address.city,
          state: this.adminReg.address.state,
          country: this.adminReg.address.country,
          zipCode: this.adminReg.address.zipCode
        }
      }
      return payload
    },
    async editAdmin() {
      let orgID = sessionStorage.getItem("userOrgID")
      if (orgID === null || orgID === "null") {
        orgID = JSON.parse(localStorage.getItem("currentOrg")).id
      }
      const payload = {
        id: this.adminReg.id,
        orgID: orgID,
        person: {
          id: this.adminReg.person.id,
          firstName: this.adminReg.person.firstName,
          lastName: this.adminReg.person.lastName,
          email: this.adminReg.person.email,
          cellNumber: this.adminReg.person.cellPhone,
          gender: this.adminReg.person.gender
        },
        status: 2,
        address: {
          id: this.adminReg.address.id,
          address1: this.adminReg.address.address1,
          address2: this.adminReg.address.address2,
          city: this.adminReg.address.city,
          state: this.adminReg.address.state.toString(),
          country: this.adminReg.address.country.toString(),
          zipCode: this.adminReg.address.zipCode
        }
      }
      const response = await this.updateAdmin({ admin: payload, id: this.adminReg.id })
      return response
    },
    async saveAdmin() {
      try {
        const admin = this.definePayload()
        const response = await this.createAdmin({ admin })
        return response
      } catch (error) {
        console.log('error', error)
        const message = this.isEdit ? "updating" : "saving"
        const errorMsg = error.message || `An error occurred while ${message} the admin.`
        Swal.fire({
          title: "Error!",
          text: errorMsg,
          icon: "error"
        })
        throw new Error(errorMsg)
      }
    },
    async handleSubmit() {
      try {
        if (this.isEdit) {
          await this.editAdmin()
          this.successfulMsg("updated")
        }
        if (!this.isEdit) {
          const orgsData = sessionStorage.getItem("organizations")
          const organizations = JSON.parse(orgsData)
          if (organizations.length > 1) {
            this.isOrganizationModalOpen = true
            return
          } else if (organizations.length === 1) {
            this.selectedOrganization = [organizations[0].orgId]
            await this.saveAdmin()
            this.successfulMsg("saved")
          } else {
            Swal.fire({
              title: "Error!",
              text: "No organizations available.",
              icon: "error"
            })
            return
          }
        }
      } catch (error) {
        console.log('error', error)
        const message = this.isEdit ? "updating" : "saving"
        const errorMsg = error.message || `An error occurred while ${message} the admin.`
        Swal.fire({
          title: "Error!",
          text: errorMsg,
          icon: "error"
        })
      }
    },
    async handleEdit(id) {
      try {
        this.isLoadingAdmin = true
        const response = await this.listAdminById({ id })
        this.isLoadingAdmin = false
        this.isEdit = true
        this.isModalOpen = true
        const detailedAdmin = response.admin
        const admin = {
          id: detailedAdmin.ID,
          orgID: detailedAdmin.orgID,
          person: {
            id: detailedAdmin.person.id,
            firstName: detailedAdmin.person.firstName,
            lastName: detailedAdmin.person.lastName,
            email: detailedAdmin.person.email,
            gender: detailedAdmin.person.gender,
            cellPhone: detailedAdmin.person.cellNumber
          },
          status: detailedAdmin.status,
          address: {
            id: detailedAdmin.address.id || "",
            address1: detailedAdmin.address.address1,
            address2: detailedAdmin.address.address2,
            city: detailedAdmin.address.city,
            state: detailedAdmin.address.state,
            country: detailedAdmin.address.country,
            zipCode: detailedAdmin.address.zipCode
          }
        }
        this.$store.dispatch("setAdminReg", admin)
      } catch (error) {
        this.isLoadingAdmin = false
        console.error(error)
        Swal.fire({
          title: "Error!",
          text: "An error occurred while fetching the admin."
        })
      }
    },
    async handleRemove(id) {
      const dialog = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      })
      if (dialog.value) {
        try {
          await this.removeAdmin({ id })
          Swal.fire({
            title: "Deleted!",
            text: "Admin has been deleted."
          })
          this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
        } catch (error) {
          Swal.fire({
            title: "Error!",
            text: "An error occurred while deleting the admin.",
            icon: "error"
          })
        }
      }
    },
    handleClose() {
      this.isModalOpen = false
    },
    handleAdd() {
      this.isEdit = false
      this.isModalOpen = true
      this.$store.dispatch("clearAdminReg")
    },
    handleRefresh() {
      this.offset = 0
      this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    prevPage() {
      if (this.offset > 0) {
        this.offset -= this.limit
        this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
      }
    },
    nextPage() {
      if (this.hasMoreData) {
        this.offset += this.limit
        this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
      }
    },
    async handleOrgEdit(admin) {
      try {
        this.isLoadingAdmin = true
        const response = await this.listAdminById({ id: admin.ID })
        this.isLoadingAdmin = false
        const detailedAdmin = response.admin
        this.$emit("openOrgEditModal", detailedAdmin)
      } catch (error) {
        console.error(error)
        Swal.fire({
          title: "Error!",
          text: "Failed to fetch admin details.",
          confirmButtonText: "OK"
        })
      }
    },
    async handleOrganizationSelected(organization) {
      this.selectedOrganization = organization
      this.isOrganizationModalOpen = false
      await this.saveAdmin()
      this.successfulMsg("saved")
    },
    closeOrganizationModal() {
      this.isOrganizationModalOpen = false
    },
    successfulMsg(responseMessage) {
      Swal.fire({
        title: "Success!",
        text: `Admin has been ${responseMessage}.`,
        icon: "success"
      })
      this.isModalOpen = false
      this.$store.dispatch("clearAdminReg")
      this.fetchAdmins({ search: this.searchQuery, orgId: this.currentOrganization })
    }
  }
}
</script>

<style lang="scss" scoped>
.md-table {
  max-height: 67dvh;
  overflow: auto;
}

.md-table-cell {
  padding: 2px 8px;
  font-size: 14px;
}

.text-right ::v-deep .md-table-cell-container {
  display: flex;
  justify-content: flex-end;
}

.md-table ::v-deep .md-table-head:last-child {
  text-align: left;
}

.table-shopping {
  table>thead>tr>th {
    text-transform: uppercase;
  }

  table>thead>tr>.md-table-head .md-table-head-label {
    font-size: 0.75rem;

    b {
      display: block;
      margin-bottom: 3px;
    }
  }

  .td-name {
    font-size: 1em !important;
    line-height: 1.1;

    small {
      color: green;
      font-size: 0.77em;
    }
  }

  .td-name {
    min-width: 200px;
  }

  .td-phone {
    text-align: left;
    min-width: 150px;

    small {
      margin-right: 3px;
    }
  }

  .img-container {
    width: 120px;
    max-height: 160px;
    overflow: hidden;
    display: block;

    img {
      width: 100%;
    }
  }
}

.md-table-row:hover {
  background-color: #74b5ff !important;
  color: white;
}

.table-striped {
  tbody>tr:nth-of-type(odd) {
    background-color: #edf2f7;
  }

  tbody>tr:nth-of-type(even) {
    background-color: #ffffff;
  }
}

.table-selected {
  color: white;
  background-color: #005ac2 !important;

  a,
  small {
    color: white;
  }
}

.mmodal-container {
  max-width: 70vw;
  max-height: 75vh !important;
  overflow: auto;

  margin: 0px auto;
  position: relative;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 27px 24px 0 rgba(0, 0, 0, 0.2), 0 40px 77px 0 rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  border: none;
  z-index: 9999;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
}

.pagination-container span {
  margin: 0 15px;
}

.swal2-container {
  z-index: 10000;
}
</style>
